import { create } from "zustand";
import storage from "../utils/storage";
import { JsonTryParse } from "../utils/helpers";

/**
 * useUserStore — TCSB admin authentication state.
 *
 * Auth model (BLU-004 §4, CON-001 §2):
 * - accessToken held in Zustand (memory) — NOT in localStorage long-term.
 * - currentUser persisted to localStorage for page-refresh hydration.
 * - Refresh token managed by httpOnly cookie (browser sends automatically).
 *
 * Hydration (DEF-007): the store reads currentUser/jwt from storage
 * SYNCHRONOUSLY in its initial state, so isAuthenticated is correct on the
 * very first render. This prevents the RequireAuth route guard from bouncing
 * an already-authenticated admin to /login during a post-render hydration race.
 *
 * Actions:
 * - login(user, accessToken) — sets auth state after successful /api/auth/login
 * - logout() — clears all auth state
 * - setAccessToken(token) — called by the 401 interceptor after refresh
 * - setCurrentUser(user) — called on hydration from localStorage
 */
const storedUser = JsonTryParse(storage.getItem(storage.ITEMS.currentUser));

const useUserStore = create((set) => ({
  currentUser: storedUser ?? undefined,
  accessToken: storedUser ? storage.getItem(storage.ITEMS.jwt) : null,
  isAuthenticated: !!storedUser,

  /**
   * Sets the logged-in user and access token.
   * Persists currentUser to localStorage for page-refresh hydration.
   * @param {object} user - User object from auth response
   * @param {string} token - Access token (JWT)
   */
  login: (user, token) => {
    set({ currentUser: user, accessToken: token, isAuthenticated: true });
    storage.setItem(storage.ITEMS.currentUser, JSON.stringify(user));
    storage.setItem(storage.ITEMS.jwt, token);
  },

  /**
   * Clears all auth state. Called on logout or refresh failure.
   */
  logout: () => {
    set({ currentUser: undefined, accessToken: null, isAuthenticated: false });
    storage.removeItem(storage.ITEMS.currentUser);
    storage.removeItem(storage.ITEMS.jwt);
  },

  /**
   * Updates the access token in memory. Called by the 401 interceptor after
   * a successful silent refresh. Does not persist to localStorage.
   * @param {string|null} token
   */
  setAccessToken: (token) => {
    set({ accessToken: token });
    if (token) {
      storage.setItem(storage.ITEMS.jwt, token);
    }
  },

  /**
   * Hydrates the user store from localStorage (called on app boot).
   * @param {object|undefined} user
   */
  setCurrentUser: (user) => {
    set({
      currentUser: user,
      isAuthenticated: !!user,
      // Restore token from storage on hydration
      accessToken: user ? storage.getItem(storage.ITEMS.jwt) : null,
    });
  },
}));

export default useUserStore;
